import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { SftpImportConfigurationWorkflow, ImportType, InventoryMode, DataType, ImportHierarchyOption, LibraryImportExistingInventoryModeType, LibraryImportNonSummaryRowsModeType } from '../../../store/Models/Api/SftpImportModels';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';

interface FileHandlingStepLocalProps {
    goToNextStep: (skipDefaultHandling?: boolean) => void;
    goToPrevStep: () => void;    
    configData: SftpImportConfigurationWorkflow;
    onConfigChange: (data: Partial<SftpImportConfigurationWorkflow>) => void;
}

type FileHandlingStepProps = FileHandlingStepLocalProps & ToxnotComponentProperties;

class FileHandlingStepComponent extends ToxnotComponent<FileHandlingStepProps> {    
    public handleImportTypeSelect = (importType: ImportType) => {
        const currentInventoryMode = this.props.configData.inventoryMode;
        // If switching to "Supplier" type, reset inventory mode
        const shouldResetInventoryMode = importType === ImportType.Suppliers;
        
        // If switching to Products or Materials, auto-select Multiple inventory mode
        const newInventoryMode = shouldResetInventoryMode 
            ? undefined 
            : (importType === ImportType.Products || importType === ImportType.Materials) 
                ? InventoryMode.Multiple 
                : currentInventoryMode;

        const shouldResetDataType = importType === ImportType.Suppliers && 
            (this.props.configData.dataType !== undefined || this.props.configData.dataType !== null);

        this.props.onConfigChange({
            importType: importType,
            inventoryMode: newInventoryMode,
            dataType: shouldResetDataType ? undefined : this.props.configData.dataType,
            spreadsheetHeadersToColumnIdMappings: [],
            importHierarchyOptionType: ImportHierarchyOption.LevelColumn,
            libraryImportExistingInventoryMode: LibraryImportExistingInventoryModeType.OverwriteExisting,
            libraryImportShouldUpdateSummaryRows: true,
            libraryImportNonSummaryRowsMode: LibraryImportNonSummaryRowsModeType.ReplaceNonSummaryRows
        });
    }

    public handleInventoryModeSelect = (inventoryMode: InventoryMode) => {
        this.props.onConfigChange({ 
            inventoryMode: inventoryMode,
            libraryImportExistingInventoryMode: LibraryImportExistingInventoryModeType.OverwriteExisting,
            libraryImportShouldUpdateSummaryRows: true,
            libraryImportNonSummaryRowsMode: LibraryImportNonSummaryRowsModeType.ReplaceNonSummaryRows
        });
    }

    public handleDataTypeSelect = (dataType: DataType) => {
        this.props.onConfigChange({
            dataType: dataType,
            inventoryMode: undefined,
            importType: undefined,
            spreadsheetHeadersToColumnIdMappings: []
        });
    }   
    public renderImportTypeSelection() {
        const { importType: selectedImportType, inventoryMode: selectedInventoryMode, dataType: selectedDataType } = this.props.configData;

        const importTypes = {
            [ImportType.Products]: { title: this.localizeString("Products"), desc: this.localizeString("Products are final finished goods, in most cases what your company sells.") },
            [ImportType.Materials]: { title: this.localizeString("Materials"), desc: this.localizeString("Materials are components or materials that make up your finished products.") },
            [ImportType.Suppliers]: { title: this.localizeString("Suppliers"), desc: this.localizeString("Supplier information lets you set names and contacts for suppliers so you can send Surveys.") }
        };

        const inventoryModes = {
            [InventoryMode.One]: { title: `${this.localizeString("One")} ${selectedImportType === ImportType.Products ? this.localizeString("Product") : this.localizeString("Material")}`, desc: this.localizeString(`Import data for a single Product or Material`) },
            [InventoryMode.Multiple]: { title: `${this.localizeString("Many")} ${selectedImportType === ImportType.Products ? this.localizeString("Products") : this.localizeString("Materials")}`, desc: this.localizeString("Import data for a multiple Products or Materials at once") }
        };

        const dataTypes: Record<DataType, { title: string; desc: string; extensions: string }> = {
            [DataType.Bom]: { title: this.localizeString("BOM Data"), desc: this.localizeString("A Bill of Materials spreadsheet with data on your sub-materials and ingredients, e.g. Part Name, % by weight, CASRN, Levels"), extensions: ".xlsx, .csv" },
            [DataType.Summary]: { title: this.localizeString("Compliance/Summary Data"), desc: this.localizeString("Non-BOM Data about your Product like compliance status, general description, volume sold, etc."), extensions: ".xlsx, .csv" },
            [DataType.ExternalDbIdUpdate]: { title: this.localizeString("Bulk Update External DB ID"), desc: this.localizeString("You can bulk update existing External DB ID for your products/materials by specifying the existing Part Name, Description, Supplier Name, Plant, and External DB ID, and the new External DB ID you want to update to."), extensions: ".xlsx, .csv" },
            [DataType.Scip]: { title: this.localizeString("EU SCIP Data"), desc: this.localizeString("You can bulk import EU SCIP-related data, e.g. article composition & categories."), extensions: ".xlsx, .csv" }
        };
        const orderedDataTypes: DataType[] = [DataType.Bom, DataType.Summary, DataType.Scip, DataType.ExternalDbIdUpdate];

        const showNextButton = selectedImportType === ImportType.Suppliers || selectedDataType === DataType.Scip || selectedDataType === DataType.Summary || selectedDataType === DataType.ExternalDbIdUpdate || (selectedImportType != null && selectedDataType === DataType.Bom);

        return (
            <div>
                {/* Level 1: Data Type */}
                <h4>{this.localizeString("What type of data do you want to import?")}</h4>
                <div className="selectors-container">
                    {orderedDataTypes.map(dataType => {
                        return (                            
                            <div key={dataType}
                                 className={`selector-div ${selectedDataType === dataType ? "selected" : ""}`}
                                 onClick={() => this.handleDataTypeSelect(dataType)}>
                                <div className="title">{dataTypes[dataType].title}</div>
                                <div className="description">{dataTypes[dataType].desc}</div>
                            </div>
                        );
                    })}
                    <div key={ImportType.Suppliers.toString()}
                        className={`selector-div ${selectedImportType === ImportType.Suppliers ? "selected" : ""}`}
                        onClick={() => this.handleImportTypeSelect(ImportType.Suppliers)}>
                        <div className="title">{importTypes[ImportType.Suppliers].title}</div>
                        <div className="description">{importTypes[ImportType.Suppliers].desc}</div>
                    </div>
                </div>

                {/* Level 2: Import Type */}
                {selectedDataType === DataType.Bom &&
                    <div style={{ marginTop: '20px' }}>
                        <h4>{this.localizeString("What type of import you want to perform?")}</h4>
                        <div className="selectors-container">
                            {Object.keys(importTypes).map(key => {
                                const importType = parseInt(key) as ImportType;
                                if (importType === ImportType.Suppliers)
                                    return null; // Skip Suppliers here, it's handled above
                                    return (
                                        <div key={key}
                                             className={`selector-div ${selectedImportType === importType ? "selected" : ""}`}
                                             onClick={() => this.handleImportTypeSelect(importType)}>
                                            <div className="title">{importTypes[importType].title}</div>
                                        <div className="description">{importTypes[importType].desc}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                }

                {/* Level 3: Inventory Mode Selection - Hidden, auto-selected as Multiple for Products/Materials */}

                {(selectedDataType != null || selectedImportType === ImportType.Suppliers) &&
                    <div className="alert alert-info" style={{ marginTop: '15px' }}>
                        {this.localizeString("Accepted file types:")} {selectedImportType === ImportType.Suppliers ? ".xlsx, .csv" : dataTypes[selectedDataType as keyof typeof dataTypes].extensions}
                    </div>
                }

                {(selectedImportType == ImportType.Products || selectedImportType == ImportType.Materials) &&
                    <div className="alert alert-info" style={{ marginTop: '15px' }}>
                        {this.localizeString("You can import data for a single Product or Material, or import data for multiple Products or Materials at once.")}
                    </div>
                }

                {
                    (selectedDataType === DataType.Scip) &&
                    <div>
                        <p className="start-subcontent-content-container">{this.localizeString("You can import SCIP-related data for one or many products/materials. Please use the template below for your import:")}</p>
                        <p className="start-subcontent-content-container"><a href="/files/bulk-scip-import-template.xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a></p>
                    </div>
                }

                {
                    (selectedDataType === DataType.Summary) &&
                    <div>
                        <p className="start-subcontent-content-container">{this.localizeString("Summary Data is non-BOM, non-ingredient data. e.g. Compliance determinations, general information about a Product/Material.")}</p>
                        <p className="start-subcontent-content-container">{this.localizeString("Download & fill out this import Template. The import template shows you all the fields you can import.")}</p>
                        <p className="start-subcontent-content-container"><a href="/files/Summary Data Library Import Template (One Row Per Product).xlsx" className="btn btn-default">{this.localizeString("Download Import Template")}</a></p>
                    </div>
                }

                {
                    (selectedDataType === DataType.ExternalDbIdUpdate) &&
                    <div>
                        <p className="start-subcontent-content-container">{this.localizeString("Use this import to update External DB IDs for existing Products or Materials. Matching uses Part Name, Description, Supplier Name, Plant, and Current External DB ID when provided.")}</p>
                        <p className="start-subcontent-content-container">{this.localizeString("Download & fill out the template.")}</p>
                        <p className="start-subcontent-content-container"><a href="/files/External DB ID Update Import Template.csv" className="btn btn-default">{this.localizeString("Download Import Template")}</a></p>
                    </div>
                }

                {/* Action Button */}
                <div className="text-right" style={{ marginTop: "15px" }}>
                    <button className="btn btn-default" onClick={this.props.goToPrevStep}>{this.localizeString("Back")}</button>
                    {showNextButton &&
                        <button className="btn btn-toxnot-primary" style={{ marginLeft: "10px" }} onClick={() => this.props.goToNextStep()}>{this.localizeString("Next")}</button>
                    }
                </div>
            </div>
        );
    }

    public render() {
        return this.renderImportTypeSelection();
    }
}

export const FileHandlingStep = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(FileHandlingStepComponent);
